/**
 * Typiq — le moteur de la chrome et du panier.
 *
 * Ce que les sections et les snippets du thème attendent de la page :
 *
 *   Tiroirs    [data-action="open-drawer"][data-drawer-id]  ouvre le tiroir
 *              [data-action="close-drawer"]                 le ferme
 *              Un tiroir ouvert porte aria-hidden="false", le voile .PageOverlay
 *              est visible et la page ne défile plus derrière.
 *
 *   Panier     L'événement « product:added » (envoyé par les fiches après un
 *              ajout) ou « cart:refresh » (envoyé par n'importe qui) recharge
 *              le tiroir : /cart?view=drawer est rendu par Shopify, son
 *              formulaire .Cart remplace celui de #sidebar-cart, et le
 *              compteur .Header__CartCount prend le nombre d'articles. Après
 *              un ajout, le tiroir s'ouvre.
 *              [data-action="update-item-quantity"] et [data-action="remove-item"]
 *              (data-line-id, data-quantity) passent par /cart/change.js.
 *              « cart:updated » est envoyé après chaque échange de contenu.
 *
 *   Chargement « theme:loading:start » / « theme:loading:end » allument et
 *              éteignent la barre .LoadingBar.
 *
 * Les écouteurs sont délégués sur document : le contenu du tiroir est
 * remplacé en AJAX, un écouteur posé sur un nœud ne survivrait pas.
 */
(function () {
  'use strict';

  if (window.__typiqEngine) { return; }
  window.__typiqEngine = true;

  var doc = document;
  var routes = window.routes || {};
  var cartUrl = routes.cartUrl || '/cart';
  var changeUrl = routes.cartChangeUrl || '/cart/change';

  /* ── Tiroirs ──────────────────────────────────────────────────────── */

  var openDrawer = null;
  var lockedScroll = 0;

  function overlay() { return doc.querySelector('.PageOverlay'); }

  var unlockPending = null;

  function lockPage() {
    if (unlockPending) { unlockPending(); return; }
    lockedScroll = window.pageYOffset || doc.documentElement.scrollTop || 0;
    doc.body.style.top = -lockedScroll + 'px';
    doc.documentElement.classList.add('is-drawer-open');
  }

  function unlockPage() {
    doc.documentElement.classList.remove('is-drawer-open');
    doc.body.style.top = '';
    window.scrollTo(0, lockedScroll);
  }

  function unlockAfter(drawer) {
    if (unlockPending) { unlockPending(); }
    var timer;
    var done = function (event) {
      if (event && (event.target !== drawer || event.propertyName !== 'transform')) { return; }
      cancel();
      unlockPage();
    };
    var cancel = function () {
      window.clearTimeout(timer);
      drawer.removeEventListener('transitionend', done);
      unlockPending = null;
    };
    unlockPending = cancel;
    drawer.addEventListener('transitionend', done);
    timer = window.setTimeout(done, 700);
  }

  function setExpanded(id, expanded) {
    var openers = doc.querySelectorAll('[data-action="open-drawer"][data-drawer-id="' + id + '"]');
    for (var i = 0; i < openers.length; i++) { openers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false'); }
  }

  function show(drawer) {
    if (!drawer || drawer === openDrawer) { return; }
    if (openDrawer) { hide(openDrawer, true); }
    openDrawer = drawer;
    drawer.setAttribute('aria-hidden', 'false');
    setExpanded(drawer.id, true);
    var veil = overlay();
    if (veil) { veil.classList.add('is-visible'); }
    lockPage();
    var close = drawer.querySelector('[data-action="close-drawer"]');
    if (close && typeof close.focus === 'function') { try { close.focus({ preventScroll: true }); } catch (e) {} }
    drawer.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true }));
  }

  function hide(drawer, keepLock) {
    if (!drawer) { return; }
    drawer.setAttribute('aria-hidden', 'true');
    setExpanded(drawer.id, false);
    if (drawer === openDrawer) { openDrawer = null; }
    if (!keepLock) {
      var veil = overlay();
      if (veil) { veil.classList.remove('is-visible'); }
      unlockAfter(drawer);
    }
    drawer.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
  }

  doc.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-action="open-drawer"]');
    if (opener) {
      var target = doc.getElementById(opener.getAttribute('data-drawer-id'));
      if (!target) { return; }
      event.preventDefault();
      show(target);
      return;
    }
    var closer = event.target.closest('[data-action="close-drawer"]');
    if (closer) {
      var drawer = doc.getElementById(closer.getAttribute('data-drawer-id') || '') || closer.closest('.Drawer') || openDrawer;
      if (drawer) {
        if (closer.tagName !== 'A') { event.preventDefault(); }
        hide(drawer);
      }
      return;
    }
    if (event.target.classList && event.target.classList.contains('PageOverlay') && openDrawer) {
      hide(openDrawer);
    }
  });

  doc.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openDrawer) { hide(openDrawer); }
  });

  /* ── Barre de chargement ──────────────────────────────────────────── */

  var loadingCount = 0;
  function loadingBar() { return doc.querySelector('.LoadingBar'); }
  doc.addEventListener('theme:loading:start', function () {
    loadingCount++;
    var bar = loadingBar();
    if (bar) { bar.classList.add('is-visible'); bar.style.width = '70%'; }
  });
  doc.addEventListener('theme:loading:end', function () {
    loadingCount = Math.max(0, loadingCount - 1);
    var bar = loadingBar();
    if (bar && loadingCount === 0) {
      bar.style.width = '100%';
      window.setTimeout(function () { bar.classList.remove('is-visible'); bar.style.width = '0'; }, 250);
    }
  });

  /* ── Panier ───────────────────────────────────────────────────────── */

  function setCount(count) {
    var counts = doc.querySelectorAll('.Header__CartCount');
    for (var i = 0; i < counts.length; i++) { counts[i].textContent = count; }
    var dots = doc.querySelectorAll('.Header__CartDot');
    for (var d = 0; d < dots.length; d++) { dots[d].classList[count === 0 ? 'remove' : 'add']('is-visible'); }
  }

  function drawerRoot() { return doc.getElementById('sidebar-cart'); }

  var rendering = null;

  /* Recharge le contenu du tiroir depuis le rendu serveur (cart.drawer). */
  function rerender() {
    var root = drawerRoot();
    if (!root) {
      /* Page panier : le serveur a le dernier mot. */
      window.location.reload();
      return Promise.resolve();
    }
    if (rendering) { return rendering; }
    rendering = fetch(cartUrl + '?view=drawer', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) { return response.text(); })
      .then(apply)
      ['catch'](function () {})
      .then(function () { rendering = null; });
    return rendering;
  }

  /*
   * Pose un rendu du tiroir : celui de /cart?view=drawer, ou celui que
   * Shopify renvoie avec la modification elle-même (sections/tq-cart-render,
   * paramètre « sections » de /cart/add.js et /cart/update.js).
   */
  function apply(html) {
    var root = drawerRoot();
    if (!root) { return; }
    var temp = doc.createElement('div');
    temp.innerHTML = html;
    var fresh = temp.querySelector('.Cart');
    var current = root.querySelector('.Cart');
    if (fresh && current && current.parentNode) {
      current.parentNode.replaceChild(fresh, current);
    }
    var settingsNode = temp.querySelector('[data-section-settings]');
    if (settingsNode) {
      try { setCount(JSON.parse(settingsNode.getAttribute('data-section-settings')).itemCount); } catch (e) {}
    }
    doc.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }
  window.__typiqRerenderCart = rerender;
  window.__typiqApplyCart = apply;

  doc.addEventListener('product:added', function () {
    rerender().then(function () {
      var root = drawerRoot();
      if (root && root.getAttribute('aria-hidden') !== 'false') { show(root); }
    });
  });

  doc.addEventListener('cart:refresh', function () { rerender(); });

  /* Retour depuis le cache de page : le panier a pu changer entre-temps. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && doc.querySelector('.Cart')) { rerender(); }
  });

  function changeLine(lineId, quantity) {
    return fetch(changeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: lineId, quantity: quantity })
    });
  }

  doc.addEventListener('click', function (event) {
    var control = event.target.closest('[data-action="update-item-quantity"], [data-action="remove-item"]');
    if (!control) { return; }
    var lineId = control.getAttribute('data-line-id');
    var quantity = parseInt(control.getAttribute('data-quantity'), 10);
    if (!lineId || isNaN(quantity) || quantity < 0) { return; }
    event.preventDefault();
    var inDrawer = !!control.closest('.Drawer');
    var row = control.closest('.CartItemWrapper') || control.closest('.CartItem');
    if (row) { row.classList.add('is-busy'); }
    doc.dispatchEvent(new CustomEvent('theme:loading:start'));
    changeLine(lineId, quantity)
      .then(function (response) {
        if (!response.ok) { throw new Error('change failed'); }
        return inDrawer ? rerender() : Promise.resolve(window.location.reload());
      })
      ['catch'](function () { window.location.reload(); })
      .then(function () { doc.dispatchEvent(new CustomEvent('theme:loading:end')); });
  });

  /* Quantité tapée au clavier dans la page panier : envoi à la sortie du champ. */
  doc.addEventListener('change', function (event) {
    var input = event.target.closest('.QuantitySelector__CurrentQuantity[data-line-id]');
    if (!input || input.closest('.Drawer')) { return; }
    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) { return; }
    changeLine(input.getAttribute('data-line-id'), quantity)
      .then(function () { window.location.reload(); })
      ['catch'](function () { window.location.reload(); });
  });
  /* ── Collections : « Trier », bascule de grille, pastilles ─────────── */

  var LAP = window.matchMedia ? window.matchMedia('(min-width: 1008px)') : { matches: false };
  var openPopover = null;

  function popoverFor(activator) { return doc.getElementById(activator.getAttribute('aria-controls')); }

  function placePopover(popover, activator) {
    var r = activator.getBoundingClientRect();
    var threshold = 20;
    var below = popover.clientHeight <= window.innerHeight - (r.bottom + threshold) || window.innerHeight - r.bottom >= window.innerHeight / 2;
    popover.classList.remove('Popover--positionBottom', 'Popover--positionTop', 'Popover--alignRight');
    popover.classList.add(below ? 'Popover--positionBottom' : 'Popover--positionTop', 'Popover--alignRight');
    var top = below ? r.bottom + threshold : r.top - popover.clientHeight - threshold;
    popover.setAttribute('style', 'top: ' + parseInt(top, 10) + 'px; right: ' + parseInt(window.innerWidth - r.right, 10) + 'px;');
  }

  function showPopover(popover, activator) {
    if (openPopover && openPopover !== popover) { hidePopover(openPopover); }
    openPopover = popover;
    popover.__activator = activator;
    popover.setAttribute('aria-hidden', 'false');
    activator.setAttribute('aria-expanded', 'true');
    doc.documentElement.classList.add('no-scroll');
    if (LAP.matches) {
      placePopover(popover, activator);
    } else {
      popover.removeAttribute('style');
      var veil = overlay();
      if (veil) { veil.classList.add('is-visible'); }
    }
  }

  function hidePopover(popover) {
    if (!popover) { return; }
    popover.setAttribute('aria-hidden', 'true');
    if (popover.__activator) { popover.__activator.setAttribute('aria-expanded', 'false'); }
    doc.documentElement.classList.remove('no-scroll');
    if (!LAP.matches && !openDrawer) {
      var veil = overlay();
      if (veil) { veil.classList.remove('is-visible'); }
    }
    if (openPopover === popover) { openPopover = null; }
  }

  doc.addEventListener('click', function (event) {
    var activator = event.target.closest('[aria-haspopup="true"][aria-controls]');
    if (activator) {
      var popover = popoverFor(activator);
      if (popover && popover.classList.contains('Popover')) {
        event.preventDefault();
        if (popover.getAttribute('aria-hidden') === 'false') { hidePopover(popover); } else { showPopover(popover, activator); }
        return;
      }
    }
    if (event.target.closest('[data-action="close-popover"]')) { hidePopover(openPopover); return; }
    var value = event.target.closest('.Popover [data-action="select-value"]');
    if (value) {
      var list = value.parentNode.querySelectorAll('.is-selected');
      for (var i = 0; i < list.length; i++) { list[i].classList.remove('is-selected'); }
      value.classList.add('is-selected');
      var pop = value.closest('.Popover');
      hidePopover(pop);
      if (pop.id === 'collection-sort-popover') { changeSort(value.getAttribute('data-value')); }
      return;
    }
    if (openPopover && !event.target.closest('.Popover')) {
      hidePopover(openPopover);
    }
  });

  doc.addEventListener('keyup', function (event) {
    if (event.key === 'Escape' && openPopover) { hidePopover(openPopover); }
  });

  /* Le tri recharge la grille sans recharger la page, et l'adresse suit. */
  function changeSort(sortBy) {
    var section = doc.querySelector('[data-section-type="collection"]');
    if (!section || section.getAttribute('data-sort-by') === sortBy) { return; }
    section.setAttribute('data-sort-by', sortBy);
    var url = section.getAttribute('data-collection-url') + '/?sort_by=' + encodeURIComponent(sortBy);
    if (history.pushState) { history.pushState({ path: url }, '', url); }
    doc.dispatchEvent(new CustomEvent('theme:loading:start'));
    fetch(location.pathname + '?sort_by=' + encodeURIComponent(sortBy), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var temp = doc.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector('.CollectionInner');
        var current = section.querySelector('.CollectionInner');
        if (fresh && current) { current.innerHTML = fresh.innerHTML; }
        doc.dispatchEvent(new CustomEvent('theme:loading:end'));
        var main = section.querySelector('.CollectionMain');
        var offset = main ? main.getBoundingClientRect().top : 0;
        if (offset < 0) { window.scrollBy({ top: offset, behavior: 'smooth' }); }
      })
      ['catch'](function () { window.location.href = url; });
  }

  /* Bascule de grille : colonnes changées sur place, choix gardé dans le panier. */
  doc.addEventListener('click', function (event) {
    var button = event.target.closest('[data-action="change-layout-mode"]');
    if (!button) { return; }
    var type = button.getAttribute('data-grid-type');
    var count = parseInt(button.getAttribute('data-count'), 10);
    var list = doc.querySelector('.CollectionInner .ProductList');
    if (!list || parseInt(list.getAttribute('data-' + type + '-count'), 10) === count) { return; }
    list.setAttribute('data-' + type + '-count', count);
    if (type === 'desktop') { list.setAttribute('data-tablet-count', count === 2 ? 2 : 3); }
    var siblings = button.parentNode.querySelectorAll('.CollectionToolbar__LayoutType');
    for (var i = 0; i < siblings.length; i++) { siblings[i].classList.toggle('is-active', siblings[i] === button); }
    var attributes = {};
    attributes['collection_' + type + '_items_per_row'] = count;
    fetch((routes.cartUrl || '/cart') + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: attributes })
    });
  });

  /* Pastille choisie : la carte pointe vers la teinte, et prend sa photo si elle en a une. */
  doc.addEventListener('change', function (event) {
    var radio = event.target.closest('.ProductItem .ColorSwatch__Radio');
    if (!radio) { return; }
    var item = radio.closest('.ProductItem');
    var url = radio.getAttribute('data-variant-url');
    var wrapper = item.querySelector('.ProductItem__ImageWrapper');
    var title = item.querySelector('.ProductItem__Title > a');
    if (wrapper) { wrapper.setAttribute('href', url); }
    if (title) { title.setAttribute('href', url); }
    var image = item.querySelector('.ProductItem__Image:not(.ProductItem__Image--alternate)');
    if (image && radio.hasAttribute('data-image-url') && radio.getAttribute('data-image-id') !== image.getAttribute('data-image-id')) {
      image.setAttribute('data-image-id', radio.getAttribute('data-image-id'));
      image.setAttribute('srcset', radio.getAttribute('data-image-srcset'));
      image.setAttribute('src', radio.getAttribute('data-image-url'));
      var ratio = parseFloat(radio.getAttribute('data-image-aspect-ratio')) || 1;
      image.parentNode.style.paddingBottom = (100 / ratio) + '%';
      image.parentNode.style.setProperty('--aspect-ratio', ratio);
    }
  });
})();
